// Coliseu (Desafio Solo): entrar (tentativas do dia), modificadores da temporada, marcos, loja de marcas e ranking do mês.
// Visual de arena romana: areia, dourado, louros e vermelho imperial. CSS em client/css-parts/colosseum.css (prefixo .cl-).
import { useEffect, useState } from 'preact/hooks';
import { COL_BOSS_EVERY, COL_MILESTONES, COL_MODIFIERS, COL_SHOP, colMarks } from '../../../../shared/src/game/colosseum';

export interface ColState { left: number; total: number; bought: number; marks: number; best: number; milestones: number[]; mods: string[]; buyPrice: number }
const VOCS: Array<[string, string]> = [['', 'Geral'], ['knight', 'Knight'], ['paladin', 'Paladin'], ['sorcerer', 'Sorcerer'], ['druid', 'Druid'], ['monk', 'Monk']];
const MOD_ICON: Record<string, string> = { fury: '🔥', iron: '🛡️', swarm: '🐀', haste: '💨', glass: '🔮', elite: '💀' };
const SHOP_ICON: Record<string, string> = { fragment: '💠', bestiary2x: '🧪', aura: '✨' };
const MONTHS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const n = (v: number) => v.toLocaleString('pt-BR');
const monthName = (m?: string) => { const x = /^(\d{4})-(\d{2})/.exec(m ?? ''); return x ? `${MONTHS[Number(x[2]) - 1] ?? x[2]} de ${x[1]}` : m ?? ''; };

type Row = { name: string; vocation: string; level: number; floor: number };

export function Colosseum(props: { st: ColState | null; inCity: boolean; coins: number; me?: string; send: (op: 'enter' | 'buy', id?: string) => void; onBuyAttempts: () => void }) {
  const [tab, setTab] = useState<'play' | 'shop' | 'rank'>('play');
  const [voc, setVoc] = useState('');
  const [help, setHelp] = useState(false);
  const [rank, setRank] = useState<{ month: string; rows: Row[] } | null>(null);
  useEffect(() => { if (tab === 'rank') fetch(`/api/colosseum?voc=${voc}`, { cache: 'no-cache' }).then((r) => r.json()).then(setRank).catch(() => {}); }, [tab, voc, props.st?.best, props.st?.left]);
  const st = props.st;
  if (!st) return <p class="muted">Carregando...</p>;

  const bossEvery = st.mods.includes('elite') ? 5 : COL_BOSS_EVERY;
  const nextMs = COL_MILESTONES.find((m) => !st.milestones.includes(m.floor));
  // posição da trilha: marcos igualmente espaçados, preenchimento interpolado pelo recorde
  const trail = (() => {
    const fl = COL_MILESTONES.map((m) => m.floor), seg = 100 / fl.length;
    let p = 0;
    for (let i = 0; i < fl.length; i++) {
      const a = i ? fl[i - 1]! : 0, b = fl[i]!;
      if (st.best >= b) p = (i + 1) * seg; else { p = i * seg + Math.max(0, (st.best - a) / (b - a)) * seg; break; }
    }
    return Math.min(100, p);
  })();
  const canEnter = !!st.left && props.inCity;
  const canBuy = st.bought < 1 && props.coins >= st.buyPrice;

  const rows = rank?.rows ?? [];
  const podium = rows.slice(0, 3), rest = rows.slice(3);
  const myIdx = props.me ? rows.findIndex((r) => r.name === props.me) : -1;

  return (
    <div class="cl">
      {/* cabeçalho: a arena vista de cima, com arquibancadas e o camarote do imperador */}
      <div class="cl-head">
        <div class="cl-arena" aria-hidden="true">
          <span class="cl-box" title="Camarote do imperador" />
          <span class="cl-sand"><i>⚔️</i></span>
        </div>
        <div class="cl-head-txt">
          <div class="cl-title"><span class="cl-laurel">❦</span>Coliseu de Noxara<span class="cl-laurel r">❦</span>
            <button class={'cl-help' + (help ? ' on' : '')} onClick={() => setHelp(!help)} title="Como funciona">?</button></div>
          <div class="cl-sub">Desafio solo · ondas infinitas · boss a cada <b>{bossEvery}</b> andares</div>
          <div class="cl-sub muted">{nextMs ? <>Próximo marco: <b class="cl-gold">andar {nextMs.floor}</b> ({nextMs.text})</> : <b class="cl-gold">Todos os marcos conquistados. Ave, campeão!</b>}</div>
        </div>
      </div>

      <div class="cl-tabs">
        {([['play', '🏛️ Arena'], ['shop', `Loja de marcas (${n(st.marks)})`], ['rank', 'Ranking do mês']] as const).map(([id, l]) =>
          <button key={id} class={'tb-button' + (tab === id ? ' green' : '')} onClick={() => setTab(id)}>{l}</button>)}
      </div>

      {tab === 'play' && <div class="cl-play">
        {(help || st.best === 0) && (
          <div class="cl-steps">
            <div><i>🚪</i><b>1. Entre na arena</b><small>Na cidade, gaste uma tentativa do dia. Só funciona com o jogo aberto.</small></div>
            <div><i>⚔️</i><b>2. Sobreviva às ondas</b><small>A cada andar os monstros ficam mais fortes e numerosos; a cada {bossEvery} andares, um boss. Sem XP e sem loot; os supplies são gastos.</small></div>
            <div><i>🏅</i><b>3. Ganhe marcas</b><small>Cada andar concluído rende marcas (andar 10 = {colMarks(10)}, andar 50 = {colMarks(50)}). Morrer não tem penalidade: só encerra a tentativa.</small></div>
          </div>
        )}

        {/* números em destaque */}
        <div class="cl-stats">
          <div class="cl-stat">
            <small>Tentativas hoje</small>
            <b>{st.left}<em>/{st.total}</em></b>
            <div class="cl-pips">{Array.from({ length: Math.max(st.total, st.left) }, (_, i) => <i key={i} class={i < st.left ? 'on' : ''} />)}</div>
          </div>
          <div class="cl-stat best">
            <small>Recorde pessoal</small>
            <b><em>andar</em> {st.best}</b>
            <span class="cl-statnote">{st.best ? 'Melhor de todos os tempos' : 'Ainda sem recorde'}</span>
          </div>
          <div class="cl-stat marks">
            <small>Marcas do Coliseu</small>
            <b class="cl-gold">{n(st.marks)}</b>
            <span class="cl-statnote">Gaste na loja de marcas</span>
          </div>
        </div>

        <div class="cl-actions">
          <button class="tb-button green cl-enter" disabled={!canEnter} onClick={() => props.send('enter')}>{!props.inCity ? 'Volte para a cidade' : st.left ? '⚔️ Entrar no Coliseu' : 'Sem tentativas hoje'}</button>
          <button class="tb-button" disabled={!canBuy} onClick={props.onBuyAttempts}
            title={st.bought >= 1 ? 'Você já comprou hoje' : props.coins < st.buyPrice ? `Você tem ${n(props.coins)} coins` : ''}>
            +3 tentativas ({st.buyPrice} coins, 1 vez por dia)</button>
        </div>

        {/* modificadores do mês como selos de cera */}
        <div class="cl-sec">Decretos do imperador <small class="muted">· modificadores deste mês</small></div>
        <div class="cl-mods">
          {st.mods.map((id) => {
            const m = COL_MODIFIERS.find((x) => x.id === id);
            return m ? (
              <div key={id} class="cl-mod" title={m.desc}>
                <span class="cl-seal">{MOD_ICON[id] ?? '⚜'}</span>
                <div><b>{m.name}</b><small>{m.desc}</small></div>
              </div>
            ) : null;
          })}
          {!st.mods.length && <small class="muted">Nenhum modificador ativo.</small>}
        </div>

        {/* trilha dos marcos de primeira vez */}
        <div class="cl-sec">Marcos de primeira vez <small class="muted">· recompensa única ao alcançar o andar</small></div>
        <div class="cl-trail">
          <div class="cl-track"><i style={{ width: trail + '%' }} /></div>
          <div class="cl-nodes" style={{ gridTemplateColumns: `repeat(${COL_MILESTONES.length}, 1fr)` }}>
            {COL_MILESTONES.map((m) => {
              const done = st.milestones.includes(m.floor), next = nextMs?.floor === m.floor;
              return (
                <div key={m.floor} class={'cl-node' + (done ? ' done' : '') + (next ? ' next' : '')}>
                  <span class="cl-medal">{done ? '✔' : m.floor}</span>
                  <b>Andar {m.floor}</b>
                  <small>{m.text}</small>
                  <em>{done ? 'Conquistado' : next ? `Faltam ${Math.max(0, m.floor - st.best)} andares` : 'Bloqueado'}</em>
                </div>
              );
            })}
          </div>
        </div>
      </div>}

      {tab === 'shop' && <div class="cl-shop">
        <div class="cl-shop-bal">Suas marcas: <b class="cl-gold">{n(st.marks)}</b></div>
        <div class="cl-items">
          {COL_SHOP.map((it) => {
            const ok = st.marks >= it.cost;
            return (
              <div key={it.id} class={'cl-item' + (it.kind === 'aura' ? ' rare' : '')}>
                <span class="cl-item-ico">{SHOP_ICON[it.kind] ?? '🏅'}</span>
                <b>{it.name}</b>
                <small>{it.desc}</small>
                {!ok && <div class="cl-need" title={`${n(st.marks)} de ${n(it.cost)}`}><i style={{ width: Math.min(100, (st.marks / it.cost) * 100) + '%' }} /><span>faltam {n(it.cost - st.marks)}</span></div>}
                <button class="tb-button green" disabled={!ok} onClick={() => props.send('buy', it.id)}>{n(it.cost)} marcas</button>
              </div>
            );
          })}
        </div>
      </div>}

      {tab === 'rank' && <div class="cl-rank">
        <div class="cl-vocs">
          {VOCS.map(([id, l]) => <button key={id} class={'tb-button' + (voc === id ? ' green' : '')} onClick={() => setVoc(id)}>{l}</button>)}
        </div>
        <small class="muted">{rank?.month ? `Temporada de ${monthName(rank.month)}. ` : ''}Zera todo mês; os melhores de cada vocation ganham cosméticos de temporada.</small>
        {!rank && <p class="muted small">Carregando...</p>}
        {rank && !rows.length && <p class="muted small">Ninguém entrou no Coliseu este mês ainda.</p>}
        {podium.length > 0 && (
          <div class="cl-podium">
            {[1, 0, 2].map((i) => {
              const r = podium[i];
              if (!r) return <div key={i} class="cl-pod empty" />;
              return (
                <div key={r.name} class={`cl-pod p${i + 1}` + (r.name === props.me ? ' me' : '')}>
                  <span class="cl-pod-medal">{i + 1}º</span>
                  <b class="cl-pod-name" title={r.name}>{r.name}</b>
                  <small>{r.vocation} · lv {r.level}</small>
                  <div class="cl-pod-step"><span>andar</span><b>{r.floor}</b></div>
                </div>
              );
            })}
          </div>
        )}
        {rest.length > 0 && (
          <div class="cl-table">
            <div class="cl-row cl-thead"><span>#</span><span>Nome</span><span>Vocation</span><span>Level</span><span>Andar</span></div>
            {rest.map((r, i) => <div key={r.name} class={'cl-row' + (r.name === props.me ? ' me' : '')}><span>{i + 4}</span><span>{r.name}</span><span>{r.vocation}</span><span>{r.level}</span><span>{r.floor}</span></div>)}
          </div>
        )}
        {rank && rows.length > 0 && props.me && (
          <div class={'cl-mine' + (myIdx >= 0 ? ' in' : '')}>
            {myIdx >= 0 ? <>Você está em <b>{myIdx + 1}º</b> com o andar <b>{rows[myIdx]!.floor}</b>. Ave!</> : <>Você ainda não aparece neste ranking. Seu recorde pessoal: andar <b>{st.best}</b>.</>}
          </div>
        )}
      </div>}
    </div>
  );
}
